// @flow
import EventEmitter from 'events';
import invariant from 'invariant';
import createDebugger from 'debug';

import type { Provider } from 'store/reducers/user/social/ISocial';

const debug = createDebugger('oauth:OAuth');

const INITIALIZATION_TIMEOUT_MS = 10000;

class OAuth extends EventEmitter {
  /**
   * The name of this provider
   */
  name: Provider;

  /**
   * Will be set to true when the sdk has finished initalizing
   * (the difference with loaded is that it initialization depends
   * on a localization string)
   */
  sdkInitialized: boolean = false;

  /**
   * True when the provider is initialized (done after receiving the proper
   * localization strings)
   */
  providerInitialized: boolean = false;

  /**
   * The default clientId to initialize the provider
   */
  defaultClientId: string;

  /**
   * @abstract
   */
  isSDKLoaded() {
    throw new Error(`abstract method #isSDKLoaded`);
  }

  /**
   * @abstract
   */
  isInitialized() {
    return this.providerInitialized;
  }

  /**
   * @abstract
   */
  isLoggedIn() {
    throw new Error(`abstract method #isLoggedIn`);
  }

  /**
   * Requires the personId because of the facebook SDK limitation,
   * see the facebook provider login method
   *
   * @abstract
   */
  login(personId?: string): Promise<any> {
    throw new Error(`abstract method #login`);
  }

  /**
   * @abstract
   */
  logout(): Promise<any> {
    throw new Error(`abstract method #logout`);
  }

  /**
   * A user that signed in through social login is valid to match when it has an
   * email, a facebook account can be created with a phone number, if so then
   * this user is not valid for our app
   *
   * @abstract
   */
  async isUserValid(): Promise<boolean> {
    await this.waitForSDK();
    if (await this.isLoggedIn()) {
      const profile = await this.getProfile();
      // email might return in case of facebook, unfortunately the fake facebook
      // accounts that I created are immediately marked to review but
      // the simple @ symbol check will work fine
      return profile.email && profile.email.indexOf('@') > -1;
    }
    return false;
  }

  /**
   * @abstract
   */
  getLoginStatus(): Promise<any> {
    throw new Error(`abstract method #getLoginStatus`);
  }

  /**
   * @abstract
   */
  getToken(): Promise<any> {
    throw new Error('abstract method #getToken');
  }

  /**
   * @abstract
   */
  getProfile(): Promise<any> {
    throw new Error(`abstract method #getProfile`);
  }

  getName(): Provider {
    invariant(this.name, 'this.name is not defined');
    return this.name;
  }

  /**
   * Entry point (see middlewares/oauth)
   */
  async initialize(clientId?: string) {
    invariant(this.defaultClientId, 'this.defaultClientId is not defined');
    if (!this.sdkInitialized) {
      const computedClientId = clientId || this.defaultClientId;
      if (computedClientId === this.defaultClientId) {
        // prettier-ignore
        debug(`oauth provider ${this.name} is using the defaultClientId: ${this.defaultClientId }`);
      }
      await this.doInitialize(computedClientId);
      this.sdkInitialized = true;
    }
  }

  /**
   * @abstract
   */
  doInitialize(...args: any[]) {
    throw new Error(`abstract method #doInitialize`);
  }

  /**
   * A promise resolved when the SDK is finally initialized
   * or thrown when it timeouts
   */
  waitForSDK(): Promise<void> {
    const self = this;
    const timeout = new Promise((_, reject) =>
      setTimeout(reject, INITIALIZATION_TIMEOUT_MS)
    );
    const initializer = new Promise(function loop(resolve, reject) {
      if (self.isSDKLoaded()) resolve();
      // TODO(mauricio): this could actually be captured through an event
      setTimeout(() => loop(resolve, reject), 100);
    });
    // wait for the timeout to fail or for the sdk to load
    return Promise.race([timeout, initializer]);
  }

  /**
   * Checks that the SDK is initialized (and obviously loaded)
   *
   * @throws If the SDK is not initialized
   */
  throwIfNotInitialized() {
    if (!this.sdkInitialized) {
      throw new Error('SDK is not initialized');
    }
  }
}

export default OAuth;



// WEBPACK FOOTER //
// ./src/utils/oauth/OAuth.js